import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bkkAt, bkkNow, fmtBkk, toBkkInput, fromBkkInput } from '../../utils/bkkTime';

// The ▾ beside "Send Now" (Bugify "Schedule Send Emails", Calem P0). Shown only when the sender is
// the user's own personal mailbox (the composer decides). Gmail's presets in Bangkok time —
// Tomorrow morning 08:00 · Tomorrow afternoon 13:00 · Monday morning 08:00 — plus a date-time box.
// One entry per moment: on a Sunday "Monday morning" IS tomorrow morning, so it is not listed twice.
function presets(): { key: string; at: number }[] {
  const { dow } = bkkNow();
  const out: { key: string; at: number }[] = [];
  const tomorrowAm = bkkAt(1, 8);
  out.push({ key: 'eml_sched_tomorrow_am', at: tomorrowAm });
  out.push({ key: 'eml_sched_tomorrow_pm', at: bkkAt(1, 13) });
  const monday = bkkAt((8 - dow) % 7 || 7, 8);
  if (monday !== tomorrowAm) out.push({ key: 'eml_sched_monday', at: monday });
  return out;
}

export default function ScheduleSendMenu({ disabled = false, uiLang, onPick }: { disabled?: boolean; uiLang: string; onPick: (atMs: number) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [value, setValue] = useState('');
  const [err, setErr] = useState('');
  const [side, setSide] = useState<'left' | 'right'>('left');
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    const r = boxRef.current?.getBoundingClientRect();
    if (r) setSide(r.left + 270 > window.innerWidth ? 'right' : 'left');
    setOpen(v => !v); setCustom(false); setErr(''); setValue(toBkkInput(bkkAt(1, 8)));
  };
  const pick = (at: number) => {
    if (!(at > Date.now() + 60_000)) { setErr(t('eml_sched_past')); return; }
    setOpen(false);
    onPick(at);
  };
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 9px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-primary)', borderRadius: 6, textAlign: 'left' };
  const hover = { onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'var(--bg-active)'), onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.background = 'transparent') };

  return (
    <div ref={boxRef} style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
      {/* The arrow half of the split button: same outline as Send Now, joined to its right edge. */}
      <button onClick={toggle} disabled={disabled} title={t('eml_sched_menu')} aria-label={t('eml_sched_menu')}
        style={{ display: 'flex', alignItems: 'center', padding: '7px 7px', background: open ? 'var(--bg-active)' : 'transparent', color: 'var(--accent)', border: '1.5px solid var(--accent)', borderLeft: 'none', borderRadius: '0 var(--radius-md) var(--radius-md) 0', fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1 }}>
        <i className="ti ti-chevron-down" style={{ fontSize: 13 }} aria-hidden />
      </button>
      {open && (
        <div style={{ position: 'absolute', bottom: '100%', ...(side === 'left' ? { left: 0 } : { right: 0 }), marginBottom: 6, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', padding: 6, minWidth: 264, zIndex: 60 }}>
          <div style={{ padding: '4px 9px 6px', fontSize: 11.5, fontWeight: 600, color: 'var(--text-tertiary)' }}>{t('eml_sched_menu')}</div>
          {presets().map(p => (
            <button key={p.key} onClick={() => pick(p.at)} style={rowStyle} {...hover}>
              <span style={{ flex: 1 }}>{t(p.key)}</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtBkk(p.at, uiLang)}</span>
            </button>
          ))}
          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
          <button onClick={() => { setCustom(v => !v); setErr(''); }} style={rowStyle} {...hover}>
            <i className="ti ti-calendar-event" style={{ fontSize: 13, color: 'var(--text-tertiary)', width: 14 }} aria-hidden />
            <span style={{ flex: 1 }}>{t('eml_sched_custom')}</span>
          </button>
          {custom && (
            <div style={{ padding: '4px 9px 6px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input type="datetime-local" value={value} min={toBkkInput(Date.now())} onChange={e => { setValue(e.target.value); setErr(''); }}
                style={{ padding: '6px 8px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 11, color: err ? '#E24B4A' : 'var(--text-tertiary)' }}>{err || t('eml_sched_bkk')}</span>
                <button disabled={!value} onClick={() => pick(fromBkkInput(value))} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', opacity: !value ? 0.6 : 1 }}>
                  {t('eml_sched_schedule')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
